import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import ResultStatus from "../classes/ResultStatus";
import LabelWithInputField from "./LabelWithInputField";
import SuccessMessage from "../classes/SuccessMessage";
import EventBus from "../utils/EventBus";
import PageNavigator from "../classes/PageNavigator";
import UserType from "../classes/UserType";
import TitleWithDateField from "./TitleWithDateField";
import Result from "../classes/Result";
import NOKUser from "../models/NOKUser";
import PopupWrapperView from "./PopupWrapperView";
import AdministratorType from "../classes/AdministratorType";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import CheckServerErrors from "./CheckServerErrors";
import Administrator from "../models/Administrator";
import TextView from "./TextView";
import SearchRequest from "../models/SearchRequest";
import Hospital from "../models/Hospital";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _CancelButtonOnPressed = (d3eState: StaffAddOrEditRequestViewRefs) => void;

type _SaveButtonOnPressed = (d3eState: StaffAddOrEditRequestViewRefs) => void;

type _LabelwithInputField1OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _MidleNameInputFieldOnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField3OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _TitleWithDateFieldOnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField8OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputFieldOnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField2OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField4OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField5OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField11OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField10OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField6OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

type _LabelwithInputField7OnChanged = (
  text: string,
  d3eState: StaffAddOrEditRequestViewRefs
) => void;

export interface StaffAddOrEditRequestViewProps extends BaseUIProps {
  key?: string;
  obj: SearchRequest;
  admin: Administrator;
  user: NOKUser;
}
/// To store state data for StaffAddOrEditRequestView
class StaffAddOrEditRequestViewRefs {
  public cancelButton: CancelButtonState = new CancelButtonState();
  public saveButton: SaveButtonState = new SaveButtonState();
}

interface SaveButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: StaffAddOrEditRequestViewRefs;
  _onSaveHandler?: _SaveButtonOnPressed;
  admin: Administrator;
  user: NOKUser;
}

class SaveButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _SaveButtonWithState extends ObservableComponent<SaveButtonWithStateProps> {
  saveButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SaveButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public get saveButton(): SaveButtonState {
    return this.props.d3eState.saveButton;
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public get d3eState(): StaffAddOrEditRequestViewRefs {
    return this.props.d3eState;
  }
  public get _onSaveHandler(): _SaveButtonOnPressed {
    return this.props._onSaveHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("saveButton", null, this.saveButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "admin",
        "admin.administratorType",
        "saveButton",
        "saveButton.",
        "saveButton.hover",
        "user",
        "user.userType",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: SaveButtonWithStateProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public saveButtonOnEnter(event): void {
    return this.saveButton.setHover(true);
  }
  public saveButtonOnExit(event): void {
    return this.saveButton.setHover(false);
  }
  public dispose(): void {
    this.saveButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 100,
      child: Button({
        padding: this.saveButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.saveButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.saveButton.disable,
        onPressed: () => {
          this._onSaveHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        states: ui.joinStates(
          {
            "data-visibility":
              (this.user !== null && this.user.userType === UserType.Admin) ||
              (this.admin !== null &&
                this.admin.administratorType === AdministratorType.Admin) ||
              (this.admin !== null &&
                this.admin.administratorType === AdministratorType.Employee)
                ? false
                : true,
          },
          {}
        ),
        child: TextView({ data: "Save" }),
        onEnter: (event) => {
          this.saveButtonOnEnter(event);
        },
        onExit: (event) => {
          this.saveButtonOnExit(event);
        },
      }),
      className: "x55f hc",
      states: ui.joinStates(
        {
          "data-visibility":
            (this.user !== null && this.user.userType === UserType.Admin) ||
            (this.admin !== null &&
              this.admin.administratorType === AdministratorType.Admin) ||
            (this.admin !== null &&
              this.admin.administratorType === AdministratorType.Employee)
              ? false
              : true,
        },
        {}
      ),
    });
  }
}
function SaveButtonWithState(props: SaveButtonWithStateProps) {
  return React.createElement(_SaveButtonWithState, props);
}

interface CancelButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: StaffAddOrEditRequestViewRefs;
  _onCancelHandler?: _CancelButtonOnPressed;
}

class CancelButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _CancelButtonWithState extends ObservableComponent<CancelButtonWithStateProps> {
  cancelButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancelButton(): CancelButtonState {
    return this.props.d3eState.cancelButton;
  }
  public get d3eState(): StaffAddOrEditRequestViewRefs {
    return this.props.d3eState;
  }
  public get _onCancelHandler(): _CancelButtonOnPressed {
    return this.props._onCancelHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancelButton", null, this.cancelButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["cancelButton", "cancelButton.", "cancelButton.hover"],
      this.rebuild
    );
  }
  public cancelButtonOnEnter(event): void {
    return this.cancelButton.setHover(true);
  }
  public cancelButtonOnExit(event): void {
    return this.cancelButton.setHover(false);
  }
  public dispose(): void {
    this.cancelButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      width: 100,
      child: Button({
        padding: this.cancelButton.hover
          ? cStyle.tButtonSecondaryPaddingOnHover
          : cStyle.tButtonSecondaryPaddingOn,
        decoration: this.cancelButton.hover
          ? cStyle.tButtonSecondaryDecorationOnHover
          : cStyle.tButtonSecondaryDecorationOn,
        disable: this.cancelButton.disable,
        onPressed: () => {
          this._onCancelHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Cancel" }),
        onEnter: (event) => {
          this.cancelButtonOnEnter(event);
        },
        onExit: (event) => {
          this.cancelButtonOnExit(event);
        },
      }),
      className: "xddf hc",
    });
  }
}
function CancelButtonWithState(props: CancelButtonWithStateProps) {
  return React.createElement(_CancelButtonWithState, props);
}

class _StaffAddOrEditRequestViewState extends ObservableComponent<StaffAddOrEditRequestViewProps> {
  static defaultProps = { obj: null, admin: null, user: null };
  d3eState: StaffAddOrEditRequestViewRefs = new StaffAddOrEditRequestViewRefs();
  errors: Array<string> = ListWrapper.widget(this, "errors");
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  private _notesErrors: Array<string> = [];
  private _firstNameErrors: Array<string> = [];
  private _dateOfBirthErrors: Array<string> = [];
  public searchIdUniqueError: string;
  private _hospitalErrors: Array<string> = [];
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: StaffAddOrEditRequestViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): SearchRequest {
    return this.props.obj;
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.updateSyncProperty("admin", this.props.admin);

    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "admin",
        "admin.administratorType",
        "dateOfBirthErrors",
        "errors",
        "firstNameErrors",
        "notesErrors",
        "obj",
        "obj.addressLine1",
        "obj.addressLine2",
        "obj.city",
        "obj.dateOfBirth",
        "obj.firstName",
        "obj.isAdvancedSearch",
        "obj.lastName",
        "obj.middleName",
        "obj.mobile",
        "obj.notes",
        "obj.sSN",
        "obj.state",
        "obj.tahoeId",
        "obj.zip",
        "user",
        "user.userType",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: StaffAddOrEditRequestViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public setErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.errors, val);

    if (!isValChanged) {
      return;
    }

    this.errors.clear();

    this.errors.addAll(val);

    this.fire("errors", this);
  }
  public addToErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errors.contains(val)) this.errors.add(val);
    } else {
      this.errors.remove(this.errors.elementAt(index));

      this.errors.add(val);
    }

    this.fire("errors", this, val, true);
  }
  public removeFromErrors(val: string): void {
    this.errors.remove(val);

    this.fire("errors", this, val, false);
  }
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public get notesErrors(): Array<string> {
    return this._notesErrors ?? [];
  }
  public set notesErrors(val: Array<string>) {
    this._notesErrors.clear();

    this._notesErrors.addAll(val);
  }
  public async computeNotesErrors(): Promise<void> {
    this.notesErrors = [];

    let it: string = this.obj.notes;

    if (it == null || it === "") {
      this.notesErrors.add("Notes cannot be empty.");
    }

    this.fire("notesErrors", this);
  }
  public get firstNameErrors(): Array<string> {
    return this._firstNameErrors ?? [];
  }
  public set firstNameErrors(val: Array<string>) {
    this._firstNameErrors.clear();

    this._firstNameErrors.addAll(val);
  }
  public async computeFirstNameErrors(): Promise<void> {
    this.firstNameErrors = [];

    let it: string = this.obj.firstName;

    if (it == null || it === "") {
      this.firstNameErrors.add("FirstName cannot be empty.");
    }

    this.fire("firstNameErrors", this);
  }
  public get dateOfBirthErrors(): Array<string> {
    return this._dateOfBirthErrors ?? [];
  }
  public set dateOfBirthErrors(val: Array<string>) {
    this._dateOfBirthErrors.clear();

    this._dateOfBirthErrors.addAll(val);
  }
  public async computeDateOfBirthErrors(): Promise<void> {
    this.dateOfBirthErrors = [];

    let it: string = this.obj.dateOfBirth;

    if (it == null || it === "") {
      this.dateOfBirthErrors.add("Date Of Birth cannot be empty.");
    }

    this.fire("dateOfBirthErrors", this);
  }
  public async checkSearchRequestSearchIdUniqueInHospital(): Promise<void> {
    if (this.obj == null || this.obj.hospital == null) {
      return;
    }

    let isUnique: boolean =
      await RPCServices.getUniqueChecker().checkSearchRequestSearchIdUniqueInHospital(
        this.obj.hospital,
        this.obj,
        this.obj.searchId
      );

    if (!isUnique) {
      this.searchIdUniqueError = "Duplicate searchId found with this value.";
    }
  }
  public get hospitalErrors(): Array<string> {
    return this._hospitalErrors ?? [];
  }
  public set hospitalErrors(val: Array<string>) {
    this._hospitalErrors.clear();

    this._hospitalErrors.addAll(val);
  }
  public async computeHospitalErrors(): Promise<void> {
    this.hospitalErrors = [];

    let it: Hospital = this.obj.hospital;

    if (it == null) {
      this.hospitalErrors.add("Hospital cannot be null.");
    }

    this.fire("hospitalErrors", this);
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    if (this.notesErrors.isNotEmpty) {
      return false;
    }

    if (this.firstNameErrors.isNotEmpty) {
      return false;
    }

    if (this.dateOfBirthErrors.isNotEmpty) {
      return false;
    }

    if (this.hospitalErrors.isNotEmpty) {
      return false;
    }

    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    this.computeNotesErrors();

    errors.addAll(this.notesErrors);

    this.computeFirstNameErrors();

    errors.addAll(this.firstNameErrors);

    this.computeDateOfBirthErrors();

    errors.addAll(this.dateOfBirthErrors);

    this.computeHospitalErrors();

    errors.addAll(this.hospitalErrors);

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return PopupWrapperView({
      title: "Create Request",
      content: ui.Column({
        crossAxisAlignment: ui.CrossAxisAlignment.start,
        children: [
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.start,
            children: [
              LabelWithInputField({
                name: "First Name",
                placeHolder: "Enter Patient Name",
                isRequired: true,
                value: this.obj.firstName,
                errors: this.firstNameErrors,
                onChanged: (text) => {
                  this.labelwithInputField1onChanged(text, this.d3eState);
                },
                key: "0",
              }),
              LabelWithInputField({
                name: "Midle Name",
                placeHolder: "Enter Last Name",
                value: this.obj.middleName,
                onChanged: (text) => {
                  this.midleNameInputFieldonChanged(text, this.d3eState);
                },
                key: "1",
              }),
            ],
            className: "x94 hc h",
            key: "0",
          }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.start,
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              LabelWithInputField({
                name: "Last Name",
                placeHolder: "Enter Last Name",
                value: this.obj.lastName,
                onChanged: (text) => {
                  this.labelwithInputField3onChanged(text, this.d3eState);
                },
                key: "0",
              }),
              ui.Row({
                mainAxisAlignment: ui.MainAxisAlignment.start,
                children: [
                  TitleWithDateField({
                    name: "Date of Birth",
                    isRequired: true,
                    value: this.obj.dateOfBirth,
                    errors: this.dateOfBirthErrors,
                    onChanged: (text) => {
                      this.titleWithDateFieldonChanged(text, this.d3eState);
                    },
                    key: "0",
                  }),
                ],
                key: "1",
              }),
            ],
            className: "x736 hc h",
            key: "1",
          }),
          ui.Container({
            margin: ui.EdgeInsets.fromLTRB(5.0, 0.0, 0.0, 0.0, new Map()),
            child: LabelWithInputField({
              value: this.obj.notes,
              name: "Note",
              isRequired: true,
              errors: this.notesErrors,
              disable:
                (this.user !== null && this.user.userType === UserType.Admin) ||
                (this.admin !== null &&
                  this.admin.administratorType === AdministratorType.Admin) ||
                (this.admin !== null &&
                  this.admin.administratorType === AdministratorType.Employee),
              placeHolder: "Enter Notes",
              onChanged: (text) => {
                this.labelwithInputField8onChanged(text, this.d3eState);
              },
            }),
            key: "2",
            className: "x24f hc h",
          }),
          this.obj.isAdvancedSearch
            ? ui.Column({
                crossAxisAlignment: ui.CrossAxisAlignment.start,
                children: [
                  ui.Row({
                    mainAxisAlignment: ui.MainAxisAlignment.start,
                    children: [
                      LabelWithInputField({
                        name: "Tahoe Id",
                        value: this.obj.tahoeId,
                        placeHolder: "Enter Tahoe Id",
                        onChanged: (text) => {
                          this.labelwithInputFieldonChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "0",
                      }),
                      LabelWithInputField({
                        name: "SSN",
                        placeHolder: "Enter Email Address",
                        value: this.obj.sSN,
                        onChanged: (text) => {
                          this.labelwithInputField2onChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "1",
                      }),
                    ],
                    className: "xd3e hc h",
                    key: "0",
                  }),
                  ui.Row({
                    mainAxisAlignment: ui.MainAxisAlignment.start,
                    children: [
                      LabelWithInputField({
                        name: "Address Line1",
                        placeHolder: "Enter Address Line1",
                        value: this.obj.addressLine1,
                        onChanged: (text) => {
                          this.labelwithInputField4onChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "0",
                      }),
                      LabelWithInputField({
                        name: "Address Line2",
                        placeHolder: "Enter Address Line2",
                        value: this.obj.addressLine2,
                        onChanged: (text) => {
                          this.labelwithInputField5onChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "1",
                      }),
                    ],
                    className: "x901d hc h",
                    key: "1",
                  }),
                  ui.Row({
                    mainAxisAlignment: ui.MainAxisAlignment.start,
                    children: [
                      LabelWithInputField({
                        name: "Street",
                        placeHolder: "Enter Street",
                        isRequired: true,
                        value: this.obj.mobile,
                        onChanged: (text) => {
                          this.labelwithInputField11onChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "0",
                      }),
                      LabelWithInputField({
                        name: "City",
                        placeHolder: "Enter City",
                        isRequired: true,
                        value: this.obj.city,
                        onChanged: (text) => {
                          this.labelwithInputField10onChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "1",
                      }),
                    ],
                    className: "xfb9 hc h",
                    key: "2",
                  }),
                  ui.Row({
                    mainAxisAlignment: ui.MainAxisAlignment.start,
                    children: [
                      LabelWithInputField({
                        name: "State",
                        placeHolder: "Enter State",
                        isRequired: true,
                        value: this.obj.state,
                        onChanged: (text) => {
                          this.labelwithInputField6onChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "0",
                      }),
                      LabelWithInputField({
                        name: "Zip Code",
                        placeHolder: "Enter Zip Code",
                        isRequired: true,
                        value: this.obj.zip,
                        onChanged: (text) => {
                          this.labelwithInputField7onChanged(
                            text,
                            this.d3eState
                          );
                        },
                        key: "1",
                      }),
                    ],
                    className: "x963 hc h",
                    key: "3",
                  }),
                ],
                className: "xe0a hc h",
              })
            : [],
        ],
        className: "x7f hc h",
      }),
      buttons: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            this.errors.isNotEmpty
              ? CheckServerErrors({ errors: this.errors })
              : [],
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                CancelButtonWithState({
                  d3eState: this.d3eState,
                  _onCancelHandler: this.onCancelHandler,
                  key: "0",
                }),
                SaveButtonWithState({
                  d3eState: this.d3eState,
                  _onSaveHandler: this.onSaveHandler,
                  admin: this.admin,
                  user: this.user,
                  key: "1",
                }),
              ],
              key: "1",
            }),
          ],
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "StaffAddOrEditRequestView hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onCancelHandler = (d3eState: StaffAddOrEditRequestViewRefs): void => {
    this.obj.restore();

    this.navigator.close();
  };
  public onSaveHandler = async (
    d3eState: StaffAddOrEditRequestViewRefs
  ): Promise<void> => {
    if (this.validate().isEmpty) {
      let result: Result<SearchRequest> = await this.obj.save();

      if (result.status === ResultStatus.Success) {
        EventBus.get().fire(
          new SuccessMessage({ message: "Successfully Saved" })
        );

        this.navigator.close();
      } else {
        this.setErrors(result.errors);
      }
    }
  };
  public labelwithInputField1onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setFirstName(val);
  };
  public midleNameInputFieldonChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setMiddleName(val);
  };
  public labelwithInputField3onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setLastName(val);
  };
  public titleWithDateFieldonChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setDateOfBirth(val);
  };
  public labelwithInputField8onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setNotes(val);
  };
  public labelwithInputFieldonChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setTahoeId(val);
  };
  public labelwithInputField2onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setSSN(val);
  };
  public labelwithInputField4onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setAddressLine1(val);
  };
  public labelwithInputField5onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setAddressLine2(val);
  };
  public labelwithInputField11onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setMobile(val);
  };
  public labelwithInputField10onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setCity(val);
  };
  public labelwithInputField6onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setState(val);
  };
  public labelwithInputField7onChanged = (
    val: string,
    d3eState: StaffAddOrEditRequestViewRefs
  ): void => {
    this.obj.setZip(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get cancelButton() {
    return this.d3eState.cancelButton;
  }
  public get saveButton() {
    return this.d3eState.saveButton;
  }
}
export default function StaffAddOrEditRequestView(
  props: StaffAddOrEditRequestViewProps
) {
  return React.createElement(_StaffAddOrEditRequestViewState, {
    ..._StaffAddOrEditRequestViewState.defaultProps,
    ...props,
  });
}
